import {
  type ClientClinicalDigestResponse,
  clientClinicalDigestQueryKey,
  useClientClinicalDigestQuery,
  useCurrentUserQuery,
  usePublishClientClinicalDigestMutation,
  useUnpublishClientClinicalDigestMutation,
} from '@cartwheel/api/v1'
import { useQueryClient } from '@tanstack/react-query'
import ClinicalDigestCard, {
  type ClinicalDigest as ClinicalDigestData,
} from './clinical-digest-card'

interface Props {
  clientId: string
}

export default function ClinicalDigest({ clientId }: Props) {
  const queryClient = useQueryClient()
  const { data: currentUser } = useCurrentUserQuery()
  const canPublishDigests = currentUser?.permissions?.publish_clinical_digests ?? false

  const queryKey = clientClinicalDigestQueryKey({ path: { client_id: clientId } })
  const { data, isPending, error } = useClientClinicalDigestQuery({
    path: { client_id: clientId },
  })
  const publishMutation = usePublishClientClinicalDigestMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey })
    },
  })
  const unpublishMutation = useUnpublishClientClinicalDigestMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey })
    },
  })

  if (isPending) {
    return null
  }

  if (error) {
    return (
      <ClinicalDigestCard errorMessage="Summary is temporarily unavailable. Please try again later." />
    )
  }

  return (
    <ClinicalDigestCard
      digest={normalizeDigest(data)}
      nextUpdateDate={data.next_update_date ?? null}
      noUpcomingAppointments={data.no_upcoming_appointments}
      notEnoughData={data.not_enough_data}
      summaryNotApplicable={data.summary_not_applicable}
      hasSchoolRoi={data.has_school_roi}
      canPublishDigests={canPublishDigests}
      onPublish={() => publishMutation.mutate({ path: { client_id: clientId } })}
      onUnpublish={() => unpublishMutation.mutate({ path: { client_id: clientId } })}
      isMutating={publishMutation.isPending || unpublishMutation.isPending}
    />
  )
}

function normalizeDigest(data: ClientClinicalDigestResponse): ClinicalDigestData | null {
  if (!data.digest) {
    return null
  }

  return {
    id: data.digest.id,
    raw_response: data.digest.raw_response ?? {},
    attended_count: data.digest.attended_count ?? 0,
    no_show_count: data.digest.no_show_count ?? 0,
    cancellation_count: data.digest.cancellation_count ?? 0,
    at_risk: data.digest.at_risk ?? false,
    published_at: data.digest.published_at ?? null,
    published_by_name: data.digest.published_by_name ?? null,
    updated_at: data.digest.updated_at ?? null,
    next_update_date: data.next_update_date ?? null,
    quality_overall_score: data.digest.quality_overall_score ?? null,
    quality_verdict: data.digest.quality_verdict ?? null,
  }
}
